import React, { useState, memo, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import { Button, Grid, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material";

import { Props, PendingHEApprovalsState } from "./interfaces";
import { TableContainer, TextMessage, ActionsCellContainer, DialogContent, Container, StyledTableCell } from "./styles";
import LoadingOverlay from "../../../LoadingOverlay/LoadingOverlay";

import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import { ExtraHours } from "../../../MyCalendar/DetailedFullCalendar/styles";
import { getNormaleTime, intlFormatDateShort } from "../../../../utils/time-utils";
import CustomConfirmationChangesDialog from "../../../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WithEmployee } from "../../../../models/employees/state-employee";
import { EmployeeRef } from "../../../../models/employees/employee-ref";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCheck, faUserXmark, faUserClock } from "@fortawesome/free-solid-svg-icons";
import useAuthorization, { isAuthorizedEeGroup } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { Page } from "../../../../models/api/page";
import CustomTablePagination from "../../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import {
  approveExtraHours,
  TnAExtraHoursApiService,
} from "../../../../services/tna-extra-hours/tna-extra-hours-api.service";
import { TnAExtrahoursFilter } from "../../../../models/tna/tna-extra-hours/tna-extra-hours-filter";
import { TnAExtraHoursRef } from "../../../../models/tna";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { EmployeeGroupRef } from "../../../../models/employee-groups";

const PendingHEApprovals = ({ employee, showEe, height = 400 }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_extra_hours";
  const theme = useTheme();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const hasAccessThroughAuth = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const hasAccess = employee
    ? isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
      )
    : hasAccessThroughAuth;

  const viewOnly = isAuthorizedEeGroup(
    [AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData],
    currentUserAuthorizations,
    employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
  );

  const [state, setState] = useState<PendingHEApprovalsState>({
    loading: false,
    error: "",
    items: [],
    isMutated: false,
    mutatingStatus: "",
    confirmStatus: false,
    mutatingStatusValue: false,
    isConfirmationDialogOpen: false,
    confirmationDialogTitle: "",
    confirmationDialogMessage: "",
    confirmationDialogActionsAndHandlers: [],
    extraHours: null,
    totalItems: 0,
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
  });

  const handleSetPage = (field: keyof Page, value: Page[keyof Page]): void =>
    setState((state) => ({
      ...state,
      page: { ...state.page, [field]: value },
    }));

  const navigate = useNavigate();

  const clickNavigate = (employee: EmployeeRef | null) => {
    navigate("/my-t-and-a", {
      state: {
        employee,
        forManager: true,
      } as WithEmployee,
    });
  };

  const loadData = useCallback(
    async (id: string | null, signal?: AbortSignal): Promise<void> => {
      setState((state) => ({
        ...state,
        loading: true,
      }));
      try {
        const response = await new TnAExtraHoursApiService().loadItems(
          {
            // change later when BE will be updated as in TnA Absences
            // employeeIds: id ? [id] : [],
            ...{
              ...(!!id && {
                employeeId: id,
              }),
            },
            allStatuses: false,
          } as TnAExtrahoursFilter,
          undefined,
          undefined,
          state.page,
          undefined,
          60,
          signal
        );
        if (!response.error) {
          setState((state) => ({
            ...state,
            items: response.items,
            totalItems: response.totalItems,
            loading: false,
            error: "",
            isMutated: false,
          }));
        } else {
          setState((state) => ({
            ...state,
            items: [],
            totalItems: 0,
            loading: false,
            error: response.error,
            isMutated: false,
          }));
        }

        toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
      } catch (error) {
        setState((state) => ({
          ...state,
          loading: false,
          error: error as string,
          isMutated: false,
        }));
      }
    },
    [state.page]
  );

  const handleSaveStatus = useCallback(async () => {
    if (state.confirmStatus && state.extraHours && hasAccess) {
      setState((state) => ({
        ...state,
        loading: true,
      }));
      try {
        const isApproved =
          state.mutatingStatus === "isRejected" && !state.mutatingStatusValue
            ? false
            : state.mutatingStatus === "isApproved"
            ? !state.mutatingStatusValue
            : state.extraHours.isApproved;

        const isRejected =
          state.mutatingStatus === "isApproved" && !state.mutatingStatusValue
            ? false
            : state.mutatingStatus === "isRejected"
            ? !state.mutatingStatusValue
            : state.extraHours.isRejected;

        const { error } = await approveExtraHours(
          state.extraHours.employee?.id as string,
          state.extraHours.id,
          isApproved ? "isApproved" : "isRejected"
        );

        if (!error) {
          setState((state) => ({
            ...state,
            mutatingStatus: "",
            confirmStatus: false,
            mutatingStatusValue: false,
            isConfirmationDialogOpen: false,
            confirmationDialogTitle: "",
            confirmationDialogMessage: "",
            confirmationDialogActionsAndHandlers: [],
            extraHours: null,
            isMutated: true,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          mutatingStatus: "",
          confirmStatus: false,
          mutatingStatusValue: false,
          isConfirmationDialogOpen: false,
          confirmationDialogTitle: "",
          confirmationDialogMessage: "",
          confirmationDialogActionsAndHandlers: [],
          extraHours: null,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }
  }, [state.confirmStatus, state.mutatingStatus, state.mutatingStatusValue, state.extraHours, hasAccess]);

  const handleConfirmationCallback = useCallback(() => {
    setState((state) => ({
      ...state,
      confirmStatus: true,
    }));
  }, []);

  const handleCancelConfirmationDialog = () => {
    setState((state) => ({
      ...state,
      isConfirmationDialogOpen: false,
      confirmationDialogTitle: "",
      confirmationDialogMessage: "",
      confirmationDialogActionsAndHandlers: [],
    }));
  };

  useEffect(() => {
    state.confirmStatus && handleSaveStatus();
  }, [state.confirmStatus, handleSaveStatus]);

  useEffect(() => {
    const abortController = new AbortController();
    if (employee?.id || state.isMutated) {
      loadData(employee?.id ?? null, abortController.signal);
    } else {
      loadData(null, abortController.signal);
    }

    return () => abortController.abort();
  }, [employee?.id, state.isMutated, state.page]);

  const getWidth = (fieldTitle: string): number => {
    const field = columns.find((obj) => obj.field === fieldTitle);

    if (field) return field.width;

    return 0;
  };

  const columns = [
    {
      // title: t(`${tRP}.fields.employee`),
      title: "Employee",
      field: "employee",
      width: 25,
    },
    {
      // title: t(`${tRP}.fields.date`),
      title: "Date",
      field: "date",
      width: 25,
    },
    {
      // title: t(`${tRP}.fields.amount`),
      title: "Amount,h",
      field: "amount",
      width: 25,
    },
    {
      // title: t(`${tRP}.fields.interval`),
      title: "Interval",
      field: "interval",
      width: 25,
    },
    {
      // title: t(`${tRP}.fields.actions`),
      title: "Actions",
      field: "actions",
      width: 0,
    },
  ];

  return (
    <>
      {(hasAccess || viewOnly) && (
        <>
          <LoadingOverlay loading={state.loading}>
            <DialogContent>
              {state.items?.length === 0 && !state.loading ? (
                <Grid
                  sx={{
                    width: "100%",
                    height: `${height}px`,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography>No items</Typography>
                </Grid>
              ) : (
                <>
                  <Container
                    style={{
                      width: "100%",
                      height: `${height}px`,
                    }}
                  >
                    <TableContainer>
                      {
                        <Table
                          size={"small"}
                          stickyHeader={true}
                        >
                          <TableHead>
                            <TableRow>
                              {columns.map((column, idx) => {
                                if (!showEe && column.field === "employee") return <></>;

                                return (
                                  <StyledTableCell
                                    key={idx}
                                    align="center"
                                    width={column.width}
                                  >
                                    <Typography>{column.title}</Typography>
                                  </StyledTableCell>
                                );
                              })}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {state.items.map((item, idx) => (
                              <TableRow key={idx}>
                                {showEe && (
                                  <StyledTableCell
                                    width={getWidth("employee")}
                                    align="left"
                                  >
                                    <Grid
                                      sx={{
                                        "&:hover, &:hover .MuiTypography-root": {
                                          cursor: "pointer",
                                        },
                                      }}
                                    >
                                      <Grid
                                        onClick={() => {
                                          clickNavigate(item.employee);
                                        }}
                                        title={"Go to employee timesheet"}
                                      >
                                        {item?.employee?.name && (
                                          <Typography sx={{ fontWeight: "bold" }}>{item.employee.name}</Typography>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </StyledTableCell>
                                )}

                                <StyledTableCell
                                  width={getWidth("date")}
                                  align="center"
                                >
                                  {!!item.tnADate && (
                                    <Typography>
                                      {`${intlFormatDateShort(
                                        new Date(item.tnADate.year, item.tnADate.month - 1, item.tnADate.day)
                                      )}`}
                                    </Typography>
                                  )}
                                </StyledTableCell>

                                <StyledTableCell
                                  width={getWidth("amount")}
                                  align="center"
                                >
                                  <Grid
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ExtraHours>
                                      <Typography title={""}>{`${Number(item?.amount).toFixed(2)}`}</Typography>
                                    </ExtraHours>

                                    {item?.needAcceptance &&
                                      (item?.acceptedByClient ? (
                                        <FontAwesomeIcon
                                          icon={faUserCheck}
                                          color={"greenyellow"}
                                        />
                                      ) : item?.rejectedByClient ? (
                                        <FontAwesomeIcon
                                          icon={faUserXmark}
                                          color={theme.palette.error.main}
                                        />
                                      ) : (
                                        <FontAwesomeIcon
                                          icon={faUserClock}
                                          color={theme.palette.text.secondary}
                                        />
                                      ))}
                                  </Grid>
                                </StyledTableCell>

                                <StyledTableCell
                                  width={getWidth("interval")}
                                  align="center"
                                >
                                  <Typography>
                                    {`${getNormaleTime(item.fromHour, item.fromMinute)} - ${getNormaleTime(
                                      item.toHour,
                                      item.toMinute
                                    )}`}
                                  </Typography>
                                </StyledTableCell>

                                <StyledTableCell
                                  width={getWidth("actions")}
                                  actionsWidth={250}
                                  align="center"
                                >
                                  <ActionsCellContainer>
                                    {[
                                      {
                                        action: "reject",
                                        field: "isRejected",
                                        stateField: item.isRejected,
                                      },
                                      {
                                        action: "approve",
                                        field: "isApproved",
                                        stateField: item.isApproved,
                                      },
                                    ].map(({ action, field, stateField }, idx) => (
                                      <Button
                                        key={idx}
                                        color={field === "isApproved" ? "success" : "warning"}
                                        variant={"contained"}
                                        disabled={stateField || (viewOnly && !hasAccess)}
                                        onClick={() => {
                                          setState((state) => ({
                                            ...state,
                                            extraHours: item as TnAExtraHoursRef,
                                            mutatingStatus: field,
                                            mutatingStatusValue: stateField,
                                            isConfirmationDialogOpen: true,
                                            confirmationDialogTitle: t(`${tRP}.confirmation_dialog.titles.${action}`),
                                            confirmationDialogMessage: t(
                                              `${tRP}.confirmation_dialog.messages.${action}`
                                            ),
                                            confirmationDialogActionsAndHandlers: [
                                              {
                                                action: t(`common_words.yes`),
                                                buttonVariant: "contained",
                                                handler: handleConfirmationCallback,
                                              },
                                            ],
                                          }));
                                        }}
                                      >
                                        {t(`${tRP}.status_buttons.${action}`)}
                                      </Button>
                                    ))}
                                  </ActionsCellContainer>
                                </StyledTableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      }
                    </TableContainer>
                  </Container>
                </>
              )}
            </DialogContent>

            <CustomTablePagination
              totalItems={state.totalItems}
              pageNumber={state.page.pageNumber}
              handleChangePageNumber={(p) => handleSetPage("pageNumber", p)}
              pageSize={state.page.pageSize}
              handleChangePageSize={(s) => {
                handleSetPage("pageSize", s);
                handleSetPage("pageNumber", 0);
              }}
              // remove later
              labelRowsPerPage={"Pending approvals extra hours per page"}
            />
          </LoadingOverlay>
        </>
      )}
      {
        <CustomConfirmationChangesDialog
          open={state.isConfirmationDialogOpen}
          loading={state.loading}
          title={state.confirmationDialogTitle}
          children={<Typography>{state.confirmationDialogMessage}</Typography>}
          handleCancel={handleCancelConfirmationDialog}
          actionsAndHandlers={state.confirmationDialogActionsAndHandlers}
        />
      }
    </>
  );
};

export default memo(PendingHEApprovals);
